import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import api from "../../lib/api";
import { toast } from "sonner";
import { TrendingUp, RefreshCw, DollarSign, ShieldCheck, Wallet, Receipt, Download, ExternalLink, Banknote } from "lucide-react";

/**
 * Per-merchant commercial summary.
 *
 * Pulls /admin/xpay/finance which returns:
 *   - rows[]: gross_volume, mdr_collected, reserve_held,
 *             reserve_releasable_now, reserve_released_total,
 *             net_to_merchant, mdr_breakdown[]
 *   - totals: same fields summed
 *
 * Ops actions on this page: download CSVs, open Terms in the merchants
 * page, and (iter52) release matured rolling reserves to a merchant.
 */
export default function AdminXpayFinance() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Release-reserve modal state (iter52).
  const [releaseRow, setReleaseRow] = useState(null);
  const [releaseAmt, setReleaseAmt] = useState("");
  const [releaseNote, setReleaseNote] = useState("");
  const [releaseBusy, setReleaseBusy] = useState(false);

  const openRelease = (row) => {
    setReleaseRow(row);
    setReleaseAmt(String(row.reserve_releasable_now || 0));
    setReleaseNote("");
  };
  const closeRelease = () => {
    setReleaseRow(null); setReleaseAmt(""); setReleaseNote(""); setReleaseBusy(false);
  };
  const submitRelease = async () => {
    if (!releaseRow) return;
    const amt = Number(releaseAmt);
    if (!Number.isFinite(amt) || amt <= 0) { toast.error("Enter a positive amount."); return; }
    setReleaseBusy(true);
    try {
      const r = await api.post(`/admin/xpay/merchants/${releaseRow.merchant_id}/release-reserve`,
                                  { amount: amt, note: releaseNote });
      toast.success(`Released ${amt.toFixed(2)} ${releaseRow.currency}. Remaining: ${r.data.remaining_releasable}.`);
      closeRelease();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Release failed");
      setReleaseBusy(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/xpay/finance");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load finance data");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const downloadCsv = async (kind, merchantId = "") => {
    // kind: 'settlements' | 'transactions'
    const path = kind === "transactions"
      ? "/api/admin/reports/xpay-transactions.csv"
      : "/api/admin/reports/xpay-settlements.csv";
    const qs = new URLSearchParams();
    if (merchantId) qs.set("merchant_id", merchantId);
    const full = `${process.env.REACT_APP_BACKEND_URL}${path}${qs.toString() ? `?${qs}` : ""}`;
    try {
      const token = localStorage.getItem("xch_token");
      const resp = await fetch(full, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) { toast.error(`Failed: ${resp.status}`); return; }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const filename = (resp.headers.get("Content-Disposition") || "").match(/filename="?([^"]+)"?/)?.[1]
        || `xpay_${kind}.csv`;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch { toast.error("Download failed"); }
  };

  const fmt = (n, c = "EUR") => {
    if (n == null) return "—";
    try { return new Intl.NumberFormat(undefined, { style: "currency", currency: c }).format(n); }
    catch { return `${n} ${c}`; }
  };
  const t = data?.totals || {};

  return (
    <AdminLayout
      title="XPay finance"
      subtitle="Commercial roll-up — gross, MDR collected, rolling reserve held, net to merchant. Editable terms live under Xchangely Pay → Terms."
    >
      <div className="flex flex-wrap justify-end gap-2 mb-4">
        <Button onClick={() => navigate("/admin/xpay-merchants")} variant="ghost" size="sm" className="rounded-xl gap-1.5"
                data-testid="xpay-finance-jump-merchants">
          <ExternalLink className="w-3.5 h-3.5" /> Merchants & terms
        </Button>
        <Button onClick={() => navigate("/admin/xpay-transactions")} variant="ghost" size="sm" className="rounded-xl gap-1.5"
                data-testid="xpay-finance-jump-transactions">
          <Receipt className="w-3.5 h-3.5" /> All transactions
        </Button>
        <Button onClick={() => downloadCsv("settlements")} variant="outline" size="sm" className="rounded-xl gap-1.5"
                data-testid="xpay-finance-csv-settlements">
          <Download className="w-3.5 h-3.5" /> Settlements CSV
        </Button>
        <Button onClick={() => downloadCsv("transactions")} variant="outline" size="sm" className="rounded-xl gap-1.5"
                data-testid="xpay-finance-csv-transactions">
          <Download className="w-3.5 h-3.5" /> Transactions CSV
        </Button>
        <Button onClick={load} variant="outline" size="sm" className="rounded-xl gap-1.5"
                data-testid="xpay-finance-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </Button>
      </div>

      {/* KPI tiles */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <KPI label="Gross volume"       value={fmt(t.gross_volume)}            icon={TrendingUp}  tone="indigo"  tdid="kpi-gross" />
        <KPI label="MDR collected"      value={fmt(t.mdr_collected)}           icon={DollarSign}  tone="emerald" tdid="kpi-mdr" />
        <KPI label="Reserve held"       value={fmt(t.reserve_held)}            icon={ShieldCheck} tone="amber"   tdid="kpi-reserve" />
        <KPI label="Releasable now"     value={fmt(t.reserve_releasable_now)}  icon={Banknote}    tone="lime"    tdid="kpi-releasable" />
        <KPI label="Net to merchants"   value={fmt(t.net_to_merchant)}         icon={Wallet}      tone="slate"   tdid="kpi-net" />
      </div>

      {/* Per-merchant table */}
      <div className="xch-card overflow-hidden" data-testid="xpay-finance-table">
        <div className="px-5 py-3.5 border-b border-border flex items-center gap-2">
          <Receipt className="w-4 h-4 text-indigo-700" />
          <span className="font-display font-bold">Per merchant</span>
          <Badge variant="outline" className="ml-2 text-[10px] tabular-nums">
            {data?.rows?.length || 0} merchants · {t.link_count || 0} settled links
          </Badge>
        </div>
        {loading ? (
          <div className="px-5 py-12 text-center text-sm text-muted-foreground">Loading…</div>
        ) : !data?.rows?.length ? (
          <div className="px-5 py-12 text-center text-sm text-muted-foreground">
            No settled volume yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="text-left px-4 py-2">Merchant</th>
                  <th className="text-right px-3 py-2">Links</th>
                  <th className="text-right px-3 py-2">Gross</th>
                  <th className="text-right px-3 py-2">MDR %</th>
                  <th className="text-right px-3 py-2">MDR collected</th>
                  <th className="text-right px-3 py-2">Reserve %</th>
                  <th className="text-right px-3 py-2">Reserve held</th>
                  <th className="text-right px-3 py-2">Releasable</th>
                  <th className="text-right px-3 py-2">Net to merchant</th>
                  <th className="text-right px-3 py-2 w-1">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.merchant_id} className="border-t border-border hover:bg-muted/20"
                      data-testid={`xpay-finance-row-${r.merchant_id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-semibold text-sm">{r.business_name || "—"}</div>
                      <div className="text-[10px] text-muted-foreground">{r.email}</div>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{r.link_count}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{fmt(r.gross_volume, r.currency)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground" data-testid={`xpay-finance-mdr-pct-${r.merchant_id}`}>
                      {r.mdr_pct?.toFixed(2)}%
                      {Object.keys(r.mdr_by_method || {}).length > 0 && (
                        <span className="ml-1 inline-block align-middle" title={`Per-method overrides: ${Object.entries(r.mdr_by_method).map(([k,v]) => `${k}=${v}%`).join(", ")}`}>
                          <Badge variant="outline" className="text-[9px] py-0 px-1 ml-0.5" data-testid={`xpay-finance-mdr-override-${r.merchant_id}`}>per-method</Badge>
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700" data-testid={`xpay-finance-mdr-collected-${r.merchant_id}`}>{fmt(r.mdr_collected, r.currency)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">{r.rolling_reserve_pct?.toFixed(2)}%</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-amber-700">{fmt(r.reserve_held, r.currency)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-lime-700" data-testid={`xpay-finance-releasable-${r.merchant_id}`}>
                      {fmt(r.reserve_releasable_now, r.currency)}
                      {r.reserve_released_total > 0 && (
                        <div className="text-[9px] text-muted-foreground" data-testid={`xpay-finance-released-${r.merchant_id}`}>
                          {fmt(r.reserve_released_total, r.currency)} paid out
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-bold">{fmt(r.net_to_merchant, r.currency)}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <div className="inline-flex gap-1">
                        {r.reserve_releasable_now > 0 && (
                          <button
                            onClick={() => openRelease(r)}
                            className="text-[10px] font-semibold text-lime-700 hover:underline px-1.5 py-0.5 rounded"
                            title="Record a rolling-reserve payout to this merchant"
                            data-testid={`xpay-finance-release-${r.merchant_id}`}>
                            Release
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/admin/xpay-transactions?merchant_id=${r.merchant_id}`)}
                          className="text-[10px] font-semibold text-indigo-700 hover:underline px-1.5 py-0.5 rounded"
                          title="View this merchant's transactions"
                          data-testid={`xpay-finance-view-${r.merchant_id}`}>
                          View
                        </button>
                        <button
                          onClick={() => downloadCsv("transactions", r.merchant_id)}
                          className="text-[10px] font-semibold text-slate-700 hover:underline px-1.5 py-0.5 rounded"
                          title="Download this merchant's transactions CSV"
                          data-testid={`xpay-finance-csv-${r.merchant_id}`}>
                          CSV
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-foreground/20 bg-muted/30 font-bold tabular-nums">
                  <td className="px-4 py-3 text-xs uppercase tracking-wider">Total</td>
                  <td className="px-3 py-3 text-right">{t.link_count || 0}</td>
                  <td className="px-3 py-3 text-right">{fmt(t.gross_volume)}</td>
                  <td className="px-3 py-3"></td>
                  <td className="px-3 py-3 text-right text-emerald-700">{fmt(t.mdr_collected)}</td>
                  <td className="px-3 py-3"></td>
                  <td className="px-3 py-3 text-right text-amber-700">{fmt(t.reserve_held)}</td>
                  <td className="px-3 py-3 text-right text-lime-700">{fmt(t.reserve_releasable_now)}</td>
                  <td className="px-3 py-3 text-right">{fmt(t.net_to_merchant)}</td>
                  <td className="px-3 py-3"></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 🩹 Iter52 — Release reserve modal. Records a payout to the
          merchant; the actual EUR transfer happens out-of-band via SEPA. */}
      <Dialog open={!!releaseRow} onOpenChange={(o) => { if (!o) closeRelease(); }}>
        <DialogContent className="max-w-md" data-testid="xpay-release-modal">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-lime-700">
              <Banknote className="w-5 h-5" />
              Release rolling reserve
            </DialogTitle>
            <DialogDescription className="text-xs">
              Record a payout to{" "}
              <span className="font-semibold text-foreground">{releaseRow?.business_name || "—"}</span>.
              Releasable now:{" "}
              <span className="font-mono font-semibold">{fmt(releaseRow?.reserve_releasable_now, releaseRow?.currency)}</span>.
              This only updates the ledger — send the SEPA transfer separately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Amount ({releaseRow?.currency || "EUR"})</Label>
              <Input type="number" step="0.01" min="0"
                      max={releaseRow?.reserve_releasable_now ?? 0}
                      value={releaseAmt}
                      onChange={(e) => setReleaseAmt(e.target.value)}
                      className="mt-1 text-sm font-mono"
                      data-testid="xpay-release-amount" />
              <p className="text-[10px] text-muted-foreground mt-1">
                Defaults to the full releasable balance. Cap is{" "}
                {fmt(releaseRow?.reserve_releasable_now, releaseRow?.currency)}.
              </p>
            </div>
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Input type="text" value={releaseNote}
                      onChange={(e) => setReleaseNote(e.target.value)}
                      placeholder="e.g. SEPA batch 2026-02-15"
                      className="mt-1 text-sm"
                      data-testid="xpay-release-note" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={closeRelease} variant="ghost" disabled={releaseBusy}>Cancel</Button>
            <Button onClick={submitRelease} disabled={releaseBusy}
                      className="bg-lime-600 hover:bg-lime-700 text-white"
                      data-testid="xpay-release-confirm">
              {releaseBusy ? "Releasing…" : "Confirm release"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function KPI({ label, value, icon: Icon, tone, tdid }) {
  const tones = {
    indigo:  "bg-indigo-50 text-indigo-700 border-indigo-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    amber:   "bg-amber-50 text-amber-700 border-amber-100",
    lime:    "bg-lime-50 text-lime-700 border-lime-100",
    slate:   "bg-slate-50 text-slate-700 border-slate-100",
  };
  return (
    <div className={`xch-card p-4 border ${tones[tone] || tones.slate}`} data-testid={tdid}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</div>
        <Icon className="w-4 h-4 opacity-70" />
      </div>
      <div className="text-xl font-bold font-display tabular-nums">{value}</div>
    </div>
  );
}
